import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Box,
  MenuItem,
  Link,
  IconButton,
  Grid,
} from '@mui/material';
import { useCallback, useState, useEffect } from 'react';
import { TextInput } from '@ui-components/ui-library';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useLazySearchBySerialNumberQuery } from '../../store/slices/serviceConnectApiSlice';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller, useWatch} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Button } from '@ui-components/ui-library';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import en from '../../../src/language/en.json';
import { useTranslation } from '../../hooks/useTranslation';
import { useNavigate } from 'react-router-dom';
import { ROUTE_CONSTANTS } from 'constants/routeConstant';
import { CreateTicketParameters } from '../../store/slices/motorInvestigationCreateTicketApiSlice';

interface FormValues {
  serialNumber?: string | null;
  motorNumber: number;
  modelName?: string | null;
  regionName?: string | null;
  priority?: string | null;
  notes?: string | null;
  uploadFile?: File | null;
  productionDate?: string | null;
}

interface NewTicketProps {
  open: boolean;
  handleClose: () => void;
  searchSerialNumber : string;
  searchPartNumber: number | undefined;
  motorSolutionLogId?: number;
  // Whether the ticket is being created directly (default) vs. reached through the
  // search / alternative-not-found flow. Defaults to true for the direct entry points.
  isDirectTicketCreationFlow?: boolean;
}

const ticketSchema: Yup.ObjectSchema<FormValues> = Yup.object({
  serialNumber: Yup.string()
    .required('Serial Number is required')
    .matches(/^[a-zA-Z0-9]{9,12}$/, 'Enter a 9–12 character alphanumeric serial number.'),
  motorNumber: Yup.number()
    .required('Motor Number is required')
    .typeError('Motor Number must be a number')
    .test('len', 'Enter 10-digit numeric motor number', (val) => val ? val.toString().length === 10 : false),
  modelName: Yup.string().nullable().notRequired(),
  regionName: Yup.string().nullable().notRequired(),
  notes: Yup.string().nullable().notRequired(),
  productionDate: Yup.string()
  .nullable()
  .notRequired()
  .test(
    'not-in-future',
    'Future date is not valid.',
    (value) => {
      if (!value) return true; 
      const today = new Date().toISOString().split('T')[0];
      return value <= today;
    }
  ),
  priority: Yup.string().nullable().notRequired(),
  uploadFile: Yup.mixed<File>()
  .nullable()
  .notRequired()
  .test('fileType', 'jpeg, xlsx, pdf, docx, msg, eml files are allowed', (value) => {
    if (!value) return true; 
    const allowedExtensions = ['jpeg', 'xlsx', 'xls','pdf','docx','doc', 'msg', 'eml'];
    const fileExtension = value.name.split('.').pop()?.toLowerCase();
    return allowedExtensions.includes(fileExtension || '');
  }),
});

const formatDateForDisplay = (dateString: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  return dateString;
};

const getPriorityId = (priority: string): number => {
  const priorityMap: Record<string, number> = {
    breakdown: 3,
    normal: 2,
    customer: 1,
  };
  return priorityMap[priority] ?? 1;
};

const getRegionId = (region: string): number => {
  const regionMap: Record<string, number> = {
    ASC: 1,
    NSC: 2,
    RDC: 3,
    ISC: 4,
  };
  return regionMap[region] ?? 1;
};

export const NewTicket = ({ open, handleClose, searchSerialNumber = '',searchPartNumber, motorSolutionLogId, isDirectTicketCreationFlow = true}: NewTicketProps) => {
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [step, setStep] = useState<'form' | 'review'>('form');
  const [priority, setPriority] = useState('customer');
  const [type, setType] = useState('Motor');
  const [region, setRegion] = useState('ASC');
  const [fileError, setFileError] = useState<string>(''); 
  const {
    control,
    getValues,
    setValue,
    trigger,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(ticketSchema),
    defaultValues: {
      serialNumber: '',
      motorNumber: undefined,
      modelName: '',
      regionName: '',
      notes: '',
      productionDate: '',
    },
  });

  const navigate = useNavigate();

  const serialNumber = useWatch({ control, name: 'serialNumber' });
  const [searchBySerialNumber] = useLazySearchBySerialNumberQuery();
  const [isLoadingServiceConnect, setIsLoadingServiceConnect] = useState(false);
  // Tracks whether the ServiceConnect serial-number lookup returned a 200 response.
  const [isSerialNumberSearchSuccessful, setIsSerialNumberSearchSuccessful] = useState(false);

  useEffect(() => {
    const fetchServiceConnectData = async () => {
      if (serialNumber && /^[a-zA-Z0-9]{9,12}$/.test(serialNumber)) {
        setIsLoadingServiceConnect(true);
        try {
          const result = await searchBySerialNumber({ serialNumber }).unwrap();
          // Reaching here means ServiceConnect responded with a 200.
          setIsSerialNumberSearchSuccessful(true);

          if (result) {
            if(!searchPartNumber){
              setValue('motorNumber', Number(result.motorNumber));
            }
            setValue('modelName', result.modelName);
             if (result.productionDate) {
              const formattedDate = result.productionDate.split('T')[0];
              setValue('productionDate', formattedDate);
            }
          }
        } catch {
          setIsSerialNumberSearchSuccessful(false);
          console.log('Error fetching data from Service Connect');
        } finally {
          setIsLoadingServiceConnect(false);
        }
      }
    };
    fetchServiceConnectData();
  }, [serialNumber, searchBySerialNumber, setValue]);

  useEffect(() => {
    if(!open) return;
    setValue('serialNumber', searchSerialNumber);
    if(searchPartNumber!== undefined){
      setValue('motorNumber', searchPartNumber);
    }
  },[open, searchSerialNumber, searchPartNumber, setValue]);

  const handleSubmitTicket = () => {
    const formData = getValues();

    const ticketPayload: CreateTicketParameters = {
      Priority: getPriorityId(priority),
      OldMotorNumber: Number(formData.motorNumber),
      SerialNumber: formData.serialNumber ?? '',
      MachineName: formData.modelName ?? '',
      RegionId: getRegionId(region),
      ProductionDate: `${formData.productionDate}`,
      Notes: formData.notes || '',
      Files: selectedFiles || [],
    };

    handleCloseWithReset();
    navigate(`/${ROUTE_CONSTANTS.TICKET_CHECK}`, {
      state: {
        ticketPayload,
        serialNumber: formData.serialNumber,
        motorNumber: formData.motorNumber,
        motorSolutionLogId,
        isSerialNumberSearchSuccessful,
        isDirectTicketCreationFlow,
      },
    });
  };

  const handleReview = async () => {
    setValue('regionName', region);
    setValue('priority', priority);
    setValue('uploadFile', selectedFiles.length > 0 ? selectedFiles[0] : undefined);

    const valid = await trigger();
    if (valid) {
      setStep('review');
    }
  };

  const handleBack = () => {
    setStep('form');
  };

  const handleBackArrowClick = () => {
    setStep('form');
  };

  const ALLOWED_EXTENSIONS = ['xlsx', 'xls','docx','doc','jpeg', 'pdf', 'msg', 'eml'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; 

  const isValidFileExtension = (file: File): boolean => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    return ALLOWED_EXTENSIONS.includes(fileExtension || '');
  };

  const isValidFileSize = (file: File): boolean => {
    return file.size <= MAX_FILE_SIZE;
  };

  const isDuplicateFile = (file: File, existingFiles: File[]): boolean => {
    return existingFiles.some((existing) => existing.name === file.name && existing.size === file.size);
  };

  const getTotalFileSize = (files: File[]): number => {
    return files.reduce((total, file) => total + file.size, 0);
  }

  const handleFileDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragging(false);
      const files = Array.from(event.dataTransfer.files);
      if (files.length === 0) return;
      
      const validFiles: File[] = [];
      const errors: string[] = [];
      
      setSelectedFiles((prev) => {
        let currentTotalSize = getTotalFileSize(prev);

        for (const file of files) {
          if (!isValidFileExtension(file)) {
            errors.push(`"${file.name}": Invalid file type`);
            continue;
          }
          
          if (!isValidFileSize(file)) {
            errors.push(`"${file.name}": File size exceeds 10MB`);
            continue;
          }
          
          if (isDuplicateFile(file, [...prev, ...validFiles])) {
            errors.push(`"${file.name}": Duplicate file`);
            continue;
          }
          if(currentTotalSize + file.size > MAX_FILE_SIZE) {
            errors.push(`"${file.name}": Total file size exceeds 10MB`);
            continue;
          }
          currentTotalSize += file.size;
          validFiles.push(file);
        }
        
        if (errors.length > 0) {
          setFileError(errors.join('; '));
        } else {
          setFileError('');
        }
        
        return [...prev, ...validFiles];
      });
    },
    []
  );

  const handleFileSelect = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.accept = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',');
    fileInput.onchange = (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (!files || files.length === 0) return;
      
      const validFiles: File[] = [];
      const errors: string[] = [];
      
      setSelectedFiles((prev) => {
        const currentTotalSize = getTotalFileSize(prev);
        for (const file of Array.from(files)) {
          if (!isValidFileExtension(file)) {
            errors.push(`"${file.name}": Invalid file type`);
            continue;
          }
          
          if (!isValidFileSize(file)) {
            errors.push(`"${file.name}": File size exceeds 10MB`);
            continue;
          }

           if(currentTotalSize + file.size > MAX_FILE_SIZE) {
            errors.push(`"${file.name}": Total file size exceeds 10MB`);
            continue;
          }
          
          if (isDuplicateFile(file, [...prev, ...validFiles])) {
            errors.push(`"${file.name}": Duplicate file`);
            continue;
          }
          
          validFiles.push(file);
        }
        
        if (errors.length > 0) {
          setFileError(errors.join('; '));
        } else {
          setFileError('');
        }
        
        return [...prev, ...validFiles];
      });
    };
    fileInput.click();
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCloseWithReset = () => {
    reset();
    setStep('form');
    setPriority('customer');
    setType('Motor');
    setRegion('ASC');
    setSelectedFiles([]);
    setFileError('');
    handleClose();
  };

  const getLatestFormValues = () => {
    const currentValues = getValues();
    return {
      ...currentValues,
      regionName: region,
      priority: priority,
      uploadFiles: selectedFiles,
    };
  };

  return (
    <Dialog open={open} onClose={handleCloseWithReset} maxWidth="lg" fullWidth>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'left',
          gap: 3,
          mb: 1,
          boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.1)',
        }}
      >
        <DialogTitle
          sx={{ fontWeight: '700', fontSize: '16px', fontFamily: 'Inherit', flex: 1, mb: 1 }}
        >
          {step === 'form' ? (
            en.new_ticket.form_title
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={handleBackArrowClick}
                size="small"
                sx={{ color: 'inherit', height: '24px', width: '24px' }}
              >
                <ArrowBackIcon fontSize="medium" />
              </IconButton>
              <Typography sx={{ fontWeight: '700', mt: '2px' }}>
                {t('new_ticket.review_details')}
              </Typography>
            </Box>
          )}
        </DialogTitle>

        <IconButton
          disableRipple
          aria-label="close"
          size="small"
          onClick={handleCloseWithReset}
          sx={{ mr: 2, mb: 1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3 }}>
        {step === 'form' ? (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextInput
                label={t('new_ticket.type_label')}
                select
                value={type}
                onChange={(e) => setType(e.target.value)}
                required
                disabled
                sx={{ width: '100%' }}
              >
                <MenuItem value="Motor">Motor</MenuItem>
                <MenuItem value="Drive">Drive</MenuItem>
                <MenuItem value="Elektronikon">Elektronikon</MenuItem>
                <MenuItem value="Fan">Fan</MenuItem>
              </TextInput>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <Controller
                name="serialNumber"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label={t('new_ticket.serial_number_label')}
                    placeholder={t('new_ticket.placeholders.serial_number')}
                    required
                    error={!!errors.serialNumber}
                    inputProps={{ maxLength: 12 }}
                    helperText={
                      isLoadingServiceConnect
                        ? 'Loading details for other fields...'
                        : errors.serialNumber?.message
                    }
                    sx={{ width: '100%' }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <Controller
                name="motorNumber"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label={t('new_ticket.motor_number_in_machine_label')}
                    placeholder={t('new_ticket.placeholders.motor_number_in_machine')}
                    required
                    slotProps={{ htmlInput: { maxLength: 10 } }}
                    error={!!errors.motorNumber}
                    helperText={errors.motorNumber?.message}
                    sx={{ width: '100%' }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <Controller
                name="productionDate"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    type="date"
                    label={t('new_ticket.production_date_label')}
                    sx={{ width: '100%' }}
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: { max: new Date().toISOString().split('T')[0] },
                    }}
                    error={!!errors.productionDate}
                    helperText={errors.productionDate?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <Controller
                name="modelName"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label={t('new_ticket.model_name_label')}
                    placeholder={t('new_ticket.placeholders.model_name')}
                    error={!!errors.modelName}
                    helperText={errors.modelName?.message}
                    sx={{ width: '100%' }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextInput
                label={t('new_ticket.region_label')}
                select
                value={region}
                required
                helperText={
                  <Typography fontWeight="bold" sx={{ fontSize: '12px', color: 'black' }}>
                    {t('new_ticket.region_help_text')}
                  </Typography>
                }
                onChange={(e) => setRegion(e.target.value)}
                sx={{ width: '100%' }}
              >
                <MenuItem value="ASC">ASC</MenuItem>
                <MenuItem value="NSC">NSC</MenuItem>
                <MenuItem value="RDC">RDC</MenuItem>
                <MenuItem value="ISC">ISC</MenuItem>
              </TextInput>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Typography variant="body1" fontWeight={400} mb={1}>
                Upload Old Motor Data File
              </Typography>
              <Box
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleFileDrop}
                sx={{
                  minHeight: selectedFiles.length > 1 ? 60 : 146,
                  border: `1px dashed ${isDragging ? '#1976D2' : '#C4CDD5'}`,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 2,
                  py: selectedFiles.length > 1 ? 1 : 2,
                  backgroundColor: '#ffffff',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: selectedFiles.length > 1 ? 'row' : 'column',
                    alignItems: 'center',
                    gap: selectedFiles.length > 1 ? 1 : 0,
                  }}
                >
                  <Box
                    sx={{
                      background: '#E6EDEF',
                      borderRadius: '50%',
                      width: selectedFiles.length > 1 ? '28px' : '40px',
                      height: selectedFiles.length > 1 ? '28px' : '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: selectedFiles.length > 1 ? 0 : 'auto',
                      mb: selectedFiles.length > 1 ? 0 : 1.5,
                    }}
                  >
                    <UploadFileIcon
                      sx={{
                        color: '#054E5A',
                        fontSize: selectedFiles.length > 1 ? '16px' : '24px',
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      fontSize={selectedFiles.length > 1 ? '10px' : '11px'}
                    >
                      {t('new_ticket.upload.drag_text')}{' '}
                      <Link
                        onClick={handleFileSelect}
                        sx={{ fontWeight: 500, cursor: 'pointer', color: '#0069A6' }}
                      >
                        {t('new_ticket.upload.select_files')}
                      </Link>
                    </Typography>
                    {selectedFiles.length <= 1 && (
                      <Typography variant="caption" color="text.secondary" fontSize="11px">
                        {t('new_ticket.upload.format_types')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {fileError && (
                <Typography color="error" fontSize="9px" mt={0.25}>
                  {fileError}
                </Typography>
              )}

              {selectedFiles.length > 0 && (
                <Box
                  sx={{
                    mt:0.5,
                    maxHeight: 75,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  {selectedFiles.map((file, index) => (
                    <Box
                      key={`${file.name}-${index}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderRadius: 1,
                        border: '1px solid #E0E0E0',
                        px: 1.5,
                        py: 0.5,
                        backgroundColor: '#FFFFFF',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                        <UploadFileIcon sx={{ mr: 1, color: '#1976D2', flexShrink: 0, fontSize: '16px' }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '11px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {file.name}
                          </Typography>
                          {/* <Typography sx={{ fontSize: '10px', color: 'gray' }}>
                            {(file.size / 1024).toFixed(0)}kb
                          </Typography> */}
                        </Box>
                      </Box>
                      <IconButton
                        onClick={() => handleRemoveFile(index)}
                        sx={{ color: '#d32f2f', p: 0.5 }}
                        size="small"
                      >
                        <DeleteIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Typography>{t('new_ticket.notes_label')}</Typography>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    sx={{ mt: 1 }}
                    multiline
                    rows={4.8}
                    placeholder={t('new_ticket.placeholders.notes')}
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }} mt={-1}>
              <Typography fontWeight="bold">{t('new_ticket.set_priority_text')}</Typography>
              <RadioGroup row value={priority} onChange={(e) => setPriority(e.target.value)}>
                <FormControlLabel
                  value="customer" //
                  control={<Radio />}
                  label={t('new_ticket.priority.customer_inquiry_label')}
                />
                <FormControlLabel
                  value="normal"
                  control={<Radio />}
                  label={t('new_ticket.priority.general_inquiry_label')}
                />
                <FormControlLabel
                  value="breakdown"
                  control={<Radio />}
                  label={t('new_ticket.priority.breakdown_label')}
                />
              </RadioGroup>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={4} alignItems="stretch">
            {/* Left Column: Details */}
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }} display="flex">
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: '#f8f8f8',
                  borderRadius: 2,
                  p: 2,
                  margin: '0 auto',
                }}
              >
                <Grid container direction="column" rowSpacing={1}>
                  {(() => {
                    const reviewValues = getLatestFormValues();
                    return [
                      { label: en.new_ticket.type_label, value: type },
                      {
                        label: en.new_ticket.serial_number_label,
                        value: reviewValues.serialNumber,
                      },
                      {
                        label: en.new_ticket.motor_number_in_machine_label,
                        value: reviewValues.motorNumber,
                      },
                      {
                        label: en.new_ticket.production_date_label,
                        value: reviewValues.productionDate
                          ? formatDateForDisplay(reviewValues.productionDate)
                          : 'Not specified',
                      },
                      { label: en.new_ticket.model_name_label, value: reviewValues.modelName },
                      { label: en.new_ticket.region_label, value: region },
                      {
                        label: en.new_ticket.set_priority_text,
                        value:
                          priority === 'normal'
                            ? en.new_ticket.priority.general_inquiry_label
                            : priority === 'customer'
                              ? en.new_ticket.priority.customer_inquiry_label
                              : en.new_ticket.priority.breakdown_label,
                      },
                    ];
                  })().map((item, index) => (
                    <Grid size={{ xs: 12 }} key={index}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          px: 2,
                          py: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ minWidth: '180px', mr: 4 }}
                        >
                          {item.label}:
                        </Typography>
                        <Typography sx={{ minWidth: '180px', ml: 2 }} variant="body2">
                          {String(item.value)}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>

            {/* Right Column: Attachments + Notes */}
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }} display="flex">
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                {/* Attachments */}
                <Box sx={{ mb: 2 }}>
                  <Typography fontWeight="bold" mb={1}>
                    {t('new_ticket.attachment_label')}
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: selectedFiles.length > 3 ? 150 : 'auto',
                      overflowY: selectedFiles.length > 3 ? 'scroll' : 'visible',
                    }}
                  >
                    <Grid container spacing={2}>
                      {selectedFiles.length > 0 ? (
                        selectedFiles.map((file, index) => (
                          <Grid size={{ xs: 12 }} key={`${file.name}-${index}`}>
                            <Box
                              sx={{
                                backgroundColor: '#f8f8f8',
                                border: '#f8f8f8',
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                padding: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  backgroundColor: '#f5f5f5',
                                  borderRadius: 1,
                                  padding: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  ml: 1,
                                }}
                              >
                                {file.type.startsWith('image/') ? (
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    style={{
                                      maxWidth: '40px',
                                      maxHeight: '40px',
                                      borderRadius: '4px',
                                    }}
                                  />
                                ) : (
                                  <UploadFileIcon
                                    sx={{
                                      color: '#1976D2',
                                      fontSize: 20,
                                      height: '24px',
                                      width: '24px',
                                    }}
                                  />
                                )}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: '16px',
                                    display: 'block',
                                  }}
                                >
                                  {file.name}
                                </Typography>
                                <Typography
                                  fontSize={'14px'}
                                  color="text.secondary"
                                  display="block"
                                >
                                  {file.size >= 1024 * 1024
                                    ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                                    : `${(file.size / 1024).toFixed(1)} KB`}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        ))
                      ) : (
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('new_ticket.attachment_placeholder')}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Box>

                {/* Notes */}
                <Box sx={{ flexGrow: 0 }}>
                  <Typography fontWeight="bold" mb={1}>
                    {t('new_ticket.notes_text')}
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      backgroundColor: '#f8f8f8',
                      minHeight: 80,
                      maxHeight: 120,
                      overflowY: 'auto',
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    <Typography variant="body2" whiteSpace="pre-wrap">
                      {(() => {
                        const reviewValues = getLatestFormValues();
                        return reviewValues.notes?.trim()
                          ? reviewValues.notes
                          : 'No notes provided.';
                      })()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ mb: 2, mr: 2 }}>
        {step === 'form' ? (
          <>
            <Button
              variant="outlined"
              onClick={handleCloseWithReset}
              sx={{ textTransform: 'none', minWidth: '80px', height: '30px' }}
            >
              {t('new_ticket.buttons.cancel')}
            </Button>
            <Button
              endIcon={<VisibilityIcon />}
              onClick={handleReview}
              variant="solid"
              color="primary"
              sx={{
                height: '30px',
                fontSize: '16px',
                padding: '4px 16px',
                textTransform: 'none',
                minWidth: '120px',
              }}
            >
              {t('new_ticket.buttons.review_details')}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleBack}
              variant="outlined"
              sx={{ textTransform: 'none', minWidth: '80px', height: '30px' }}
            >
              {t('new_ticket.buttons.back_btn_text')}
            </Button>
            <Button
              endIcon={<SendIcon />}
              variant="solid"
              color="primary"
              sx={{
                height: '30px',
                fontSize: '16px',
                padding: '4px 16px',
                textTransform: 'none',
                minWidth: '120px',
              }}
              onClick={handleSubmitTicket}
            >
              {t('new_ticket.buttons.submit_text')}
            </Button>
          </> 
        )}
      </DialogActions>
    </Dialog>
    
  );
};

export default NewTicket;
